/**
 * The homepage's convictions: numbered statements set as a staircase.
 */
import React from "react";
import { CONTAINER, SECTION_PAD, TITLE_TO_CONTENT } from "../../ui/shell";
import { SectionTitle } from "../../ui/kit";
import { Reveal, SweepWord } from "../../ui/motion";
import { useLocales } from "../../core/i18n";

// Statements mark their accent words as [[word]].
const renderHighlighted = (text) =>
	text.split(/\[\[|\]\]/).map((part, i) =>
		i % 2 === 1 ? (
			<span key={i} className="text-pp-accent">
				{part}
			</span>
		) : (
			part
		),
	);

/** The numbered convictions from the locale catalog; nothing when the list is empty. */
const Convictions = () => {
	const { t } = useLocales();
	const convictions = /** @type {Array<{ num: string, statement: string }>} */ (
		t("homepage.convictions", { returnObjects: true }) || []
	);

	if (!convictions.length) return null;

	return (
		<section className={`bg-pp-bg ${SECTION_PAD}`}>
			<div className={CONTAINER}>
				<SectionTitle>
					{t("homepage.convictionsTitle")}
					<SweepWord dark={false}>{t("homepage.convictionsTitleHighlight")}</SweepWord>
				</SectionTitle>
				<div className={TITLE_TO_CONTENT}>
					{convictions.map((c, i) => (
						<Reveal key={c.num} delay={i * 60}>
							{/* The staircase is the section's one gesture. It only runs from
                  sm up: on a 390px screen every step stole measure from the
                  statement. Capped at 6rem so item 05 never starts mid-page. */}
							<div
								className="py-8 sm:py-10 border-t border-pp-rule last:border-b last:border-pp-rule pl-0 sm:pl-[var(--pp-step)]"
								style={
									/** @type {React.CSSProperties} */ ({
										"--pp-step": `min(${i * 1.5}vw, 7rem)`,
									})
								}
							>
								<span className="font-mono text-pp-label text-pp-muted uppercase tracking-pp-label block mb-3">
									{c.num}
								</span>
								<p className="font-sligoil font-bold text-pp-ink max-w-[62ch] text-pp-title-md">
									{renderHighlighted(c.statement)}
								</p>
							</div>
						</Reveal>
					))}
				</div>
			</div>
		</section>
	);
};

export default Convictions;
